import { AxiosResponse } from "axios";
import { endPoints } from "utils/constants";
import { crudService } from "services/crud.service";
import { NotificationManager } from "react-notifications";
import { defaultMetaObj, initialTeamState } from "store/constants";
import { getErrorMessage, getPaginatedUrlWithSearchParams } from "store/helper";
import { PayloadAction, createSlice, createAsyncThunk } from "@reduxjs/toolkit";

const { teamEndPoint } = endPoints;

export const fetchAllTeams = createAsyncThunk(
  "teams/fetchAllTeams",
  async (
    {
      page,
      limit,
      searchParamsObj = {},
    }: { page: number; limit?: number; searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      limit,
      endpoint: teamEndPoint,
      searchParams: {
        ...searchParamsObj,
      },
    });

    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response?.data;
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const deleteTeam = createAsyncThunk(
  "teams/deleteTeam",
  async ({ teamId }: any, { rejectWithValue }) => {
    try {
      const response: any = await crudService.delete(teamEndPoint, teamId);

      if (response.status === 204) {
        NotificationManager.success("Team Deleted Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const addNewTeam = createAsyncThunk(
  "teams/addNewTeam",
  async (newTeam: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.create(
        `${teamEndPoint}/`,
        newTeam
      );

      if (response.status === 201) {
        NotificationManager.success("Team Created Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const updateTeam = createAsyncThunk(
  "teams/updateTeam",
  async ({ updatedTeam, teamId }: any, { rejectWithValue }) => {
    try {
      // patch Update for team
      const response: AxiosResponse = await crudService.patch(
        teamEndPoint,
        teamId,
        updatedTeam
      );
      if (response.status === 202) {
        NotificationManager.success("Team updated Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

const teamSlice = createSlice({
  name: "teams",
  initialState: initialTeamState,
  reducers: {},
  extraReducers: (builder) => {
    // fetch all inventory
    builder
      .addCase(fetchAllTeams.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllTeams.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.teams = action.payload?.objects as any[];
          // state.teams = action?.payload.result;
          state.meta = action.payload?.meta;
        }
      )
      .addCase(
        fetchAllTeams.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.teams = [];
          state.error = action.payload as string;
          state.meta = defaultMetaObj;
        }
      );

    // delete inventory
    builder
      .addCase(deleteTeam.pending, (State) => {
        State.loading = true;
      })
      .addCase(deleteTeam.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
      })
      .addCase(
        deleteTeam.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    // add new inventory
    builder
      .addCase(addNewTeam.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(addNewTeam.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
      })
      .addCase(addNewTeam.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    //   update inventory
    builder
      .addCase(updateTeam.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        updateTeam.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(
        updateTeam.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );
  },
});

export const teamReducer = teamSlice.reducer;
